import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Header } from '@/pages/public/landing/components/Header';
import { Footer } from '@/pages/public/landing/components/Footer';
import { useGetBlogPosts } from '@/services/queries/blog';

const LIMIT = 9;

const formatDate = (iso: string) => {
	const d = new Date(iso);
	return d.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	});
};

const excerpt = (content: string, maxLen = 120) => {
	const plain = content
		.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
		.replace(/[#*_`[\]()>~]/g, '')
		.trim();
	return plain.length > maxLen ? plain.slice(0, maxLen) + '…' : plain;
};

const BlogListPage = () => {
	const navigate = useNavigate();
	const [page, setPage] = useState(1);

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
	}, [page]);
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');

	const { data, isLoading } = useGetBlogPosts({
		page,
		limit: LIMIT,
		search: search || undefined,
		sort: 'created_at',
		sort_direction: 'desc',
	});

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(1);
		setSearch(searchInput);
	};

	const totalPages = data?.pagination.totalPages ?? 1;

	return (
		<>
			<Header logoHomePath="/" />
			<main className="min-h-screen bg-white">
				{/* Hero — estilo empresa */}
				<section className="relative flex min-h-[50vh] items-center overflow-hidden bg-[#0c0c0c] px-6 py-16 text-white md:px-10 md:py-20">
					<div className="pointer-events-none absolute left-1/2 top-[-235px] h-[720px] w-[960px] -translate-x-1/2 bg-[radial-gradient(circle,rgba(225,29,116,0.22)_0%,transparent_60%)]" />
					<div className="relative z-10 mx-auto w-full max-w-[760px] text-center">
						<div className="font-albert mb-5 text-[11px] font-bold uppercase tracking-[0.22em] text-[#ff8aaa]">
							Plumaa · Conteúdo
						</div>
						<h1 className="font-libre mb-6 text-[34px] font-bold leading-[1.08] tracking-[-0.015em] md:text-[56px]">
							Artigos <em className="text-brand-pink">Plumaa</em>
						</h1>
						<p className="font-albert mx-auto mb-10 max-w-[520px] text-[17px] font-light leading-[1.7] text-white/70">
							Saúde mental, bem-estar e cuidado psicológico —
							escritos para quem quer entender mais sobre si.
						</p>

						<form
							onSubmit={handleSearch}
							className="mx-auto flex w-full max-w-lg overflow-hidden rounded-full border border-white/10 bg-white/5 backdrop-blur-sm"
						>
							<input
								type="text"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								placeholder="Buscar artigos..."
								className="flex-1 bg-transparent px-5 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none"
							/>
							<button
								type="submit"
								className="flex items-center gap-2 bg-brand-pink px-5 py-3 text-sm font-semibold transition hover:bg-brand-pink/90"
							>
								<Search className="h-4 w-4" />
								Buscar
							</button>
						</form>
					</div>
				</section>

				{/* Posts grid */}
				<section className="mx-auto max-w-6xl px-6 py-16">
					{isLoading && (
						<div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
							{Array.from({ length: LIMIT }).map((_, i) => (
								<div
									key={i}
									className="animate-pulse rounded-2xl border border-zinc-100 bg-zinc-50"
								>
									<div className="h-48 rounded-t-2xl bg-zinc-200" />
									<div className="space-y-3 p-5">
										<div className="h-4 w-1/3 rounded bg-zinc-200" />
										<div className="h-5 rounded bg-zinc-200" />
										<div className="h-4 rounded bg-zinc-100" />
									</div>
								</div>
							))}
						</div>
					)}

					{!isLoading && data?.data.length === 0 && (
						<div className="py-20 text-center text-zinc-400">
							<p className="text-lg">Nenhum artigo encontrado.</p>
							{search && (
								<button
									onClick={() => {
										setSearch('');
										setSearchInput('');
										setPage(1);
									}}
									className="mt-4 text-sm text-brand-pink underline"
								>
									Limpar busca
								</button>
							)}
						</div>
					)}

					{!isLoading && data && data.data.length > 0 && (
						<>
							<div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
								{data.data.map((post) => (
									<article
										key={post.id}
										onClick={() =>
											navigate(
												`/blog/${encodeURIComponent(
													post.slug,
												)}`,
											)
										}
										className="group cursor-pointer overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm transition hover:shadow-md"
									>
										<div className="h-48 w-full overflow-hidden bg-zinc-100">
											<img
												src={post.banner_url}
												alt={post.title}
												className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
												onError={(e) => {
													(
														e.target as HTMLImageElement
													).style.display = 'none';
												}}
											/>
										</div>
										<div className="space-y-2 p-5">
											<p className="text-xs text-zinc-400">
												{formatDate(post.created_at)}
											</p>
											<h2 className="line-clamp-2 text-base font-bold text-zinc-800 transition-colors group-hover:text-brand-pink">
												{post.title}
											</h2>
											<p className="line-clamp-3 text-sm text-zinc-500">
												{excerpt(post.content)}
											</p>
											<span className="mt-2 inline-block text-sm font-semibold text-brand-pink">
												Leia mais →
											</span>
										</div>
									</article>
								))}
							</div>

							{totalPages > 1 && (
								<div className="mt-12 flex items-center justify-center gap-3">
									<button
										onClick={() =>
											setPage((p) => Math.max(1, p - 1))
										}
										disabled={page === 1}
										className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200 text-zinc-500 transition hover:bg-zinc-100 disabled:opacity-40"
									>
										<ChevronLeft className="h-4 w-4" />
									</button>

									{Array.from(
										{ length: totalPages },
										(_, i) => i + 1,
									).map((p) => (
										<button
											key={p}
											onClick={() => setPage(p)}
											className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition ${
												p === page
													? 'bg-brand-pink text-white'
													: 'border border-zinc-200 text-zinc-600 hover:bg-zinc-100'
											}`}
										>
											{p}
										</button>
									))}

									<button
										onClick={() =>
											setPage((p) =>
												Math.min(totalPages, p + 1),
											)
										}
										disabled={page === totalPages}
										className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200 text-zinc-500 transition hover:bg-zinc-100 disabled:opacity-40"
									>
										<ChevronRight className="h-4 w-4" />
									</button>
								</div>
							)}
						</>
					)}
				</section>
			</main>
			<Footer />
		</>
	);
};

export { BlogListPage };
